/**
 * Frontend caching system for large graph data using IndexedDB
 *
 * This module provides a caching layer for graph data visualizations,
 * allowing the frontend to quickly retrieve previously generated graphs
 * without having to regenerate them or fetch from the server.
 */

const graphCache = (function () {
  // Constants for cache
  const DB_NAME = 'askFelixGraphCache';
  const DB_VERSION = 1;
  const GRAPH_STORE = 'graphs';
  const MAX_CACHE_SIZE = 500 * 1024 * 1024; // 500MB max cache size
  const MAX_CACHED_ITEMS = 20; // Maximum number of cached visualizations

  let db;
  let cacheSize = 0;
  const cacheSelf = {};

  // Initialize the database
  const initDB = () => {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = event => {
        console.error('Error opening graph cache database:', event.target.error);
        reject(event.target.error);
      };

      request.onupgradeneeded = event => {
        db = event.target.result;

        // Create an object store for the graphs
        const store = db.createObjectStore(GRAPH_STORE, { keyPath: 'id' });

        // Create indexes for faster queries
        store.createIndex('timestamp', 'timestamp', { unique: false });
        store.createIndex('size', 'size', { unique: false });
      };

      request.onsuccess = event => {
        db = event.target.result;
        console.log('Graph cache database initialized successfully');

        // Calculate current cache size
        calculateCacheSize().then(size => {
          cacheSize = size;
          console.log(`Current cache size: ${(cacheSize / (1024 * 1024)).toFixed(2)} MB`);
          resolve();
        });
      };
    });
  };

  // Calculate the total size of all cached items
  const calculateCacheSize = () => {
    return new Promise(resolve => {
      const transaction = db.transaction([GRAPH_STORE], 'readonly');
      const store = transaction.objectStore(GRAPH_STORE);
      const sizeRequest = store.index('size').openCursor();

      let totalSize = 0;

      sizeRequest.onsuccess = event => {
        const cursor = event.target.result;
        if (cursor) {
          totalSize += cursor.value.size;
          cursor.continue();
        } else {
          resolve(totalSize);
        }
      };

      sizeRequest.onerror = () => {
        console.error('Error calculating cache size');
        resolve(0);
      };
    });
  };

  // Clean up cache when it exceeds the maximum size
  const cleanupCache = async () => {
    if (cacheSize < MAX_CACHE_SIZE) {
      const transaction = db.transaction([GRAPH_STORE], 'readonly');
      const store = transaction.objectStore(GRAPH_STORE);
      const countRequest = store.count();

      const count = await new Promise(resolve => {
        countRequest.onsuccess = () => resolve(countRequest.result);
        countRequest.onerror = () => resolve(0);
      });

      if (count <= MAX_CACHED_ITEMS) {
        return; // No cleanup needed
      }
    }

    // Get items sorted by timestamp (oldest first)
    const transaction = db.transaction([GRAPH_STORE], 'readonly');
    const store = transaction.objectStore(GRAPH_STORE);
    const request = store.index('timestamp').openCursor();

    const items = await new Promise(resolve => {
      const results = [];
      request.onsuccess = event => {
        const cursor = event.target.result;
        if (cursor) {
          results.push(cursor.value);
          cursor.continue();
        } else {
          resolve(results);
        }
      };
      request.onerror = () => resolve([]);
    });

    // Sort by timestamp (oldest first)
    items.sort((a, b) => a.timestamp - b.timestamp);

    // Delete oldest items until we're under the limits
    let itemsToRemove = [];
    let sizeToRemove = 0;

    for (const item of items) {
      if (
        cacheSize - sizeToRemove <= MAX_CACHE_SIZE &&
        items.length - itemsToRemove.length <= MAX_CACHED_ITEMS
      ) {
        break;
      }

      itemsToRemove.push(item.id);
      sizeToRemove += item.size;
    }

    if (itemsToRemove.length > 0) {
      const deleteTransaction = db.transaction([GRAPH_STORE], 'readwrite');
      const deleteStore = deleteTransaction.objectStore(GRAPH_STORE);

      for (const id of itemsToRemove) {
        deleteStore.delete(id);
      }

      await new Promise(resolve => {
        deleteTransaction.oncomplete = () => {
          console.log(
            `Removed ${itemsToRemove.length} items (${(sizeToRemove / (1024 * 1024)).toFixed(2)} MB) from cache`
          );
          cacheSize -= sizeToRemove;
          resolve();
        };
        deleteTransaction.onerror = () => resolve();
      });
    }
  };

  // Update a graph's timestamp to keep it in cache longer
  const updateTimestamp = id => {
    const transaction = db.transaction([GRAPH_STORE], 'readwrite');
    const store = transaction.objectStore(GRAPH_STORE);
    const request = store.get(id);

    request.onsuccess = () => {
      const graph = request.result;
      if (graph) {
        graph.timestamp = Date.now();
        store.put(graph);
      }
    };
  };

  // Generate a unique cache ID for a graph
  const generateCacheId = (query, dashboard, graphId) => {
    return `${dashboard || 'default'}_${graphId}_${query.replace(/\s+/g, '_')}`;
  };

  // Estimate the size of a graph object in bytes
  const estimateSize = graph => {
    // Serialize to JSON and measure the string length
    const json = JSON.stringify(graph);
    // Multiply by 2 as JS strings are UTF-16 (2 bytes per character)
    // Add a buffer for IndexedDB overhead
    return json.length * 2 + 1024;
  };

  // Public API

  // Initialize the cache
  cacheSelf.init = async () => {
    try {
      await initDB();
      await cleanupCache();

      // Create the dev button if we're in dev mode
      // We call this here to ensure it's available as soon as possible
      if (typeof document !== 'undefined') {
        // Check if we're in a browser environment
        setTimeout(() => cacheSelf.createDevCacheButton(), 500); // Small delay to ensure DOM is ready
      }

      return true;
    } catch (error) {
      console.error('Failed to initialize graph cache:', error);
      return false;
    }
  };

  // Store a graph in the cache
  cacheSelf.storeGraph = async (query, dashboard, graphId, graphData) => {
    const id = generateCacheId(query, dashboard, graphId);
    const size = estimateSize(graphData);

    // If this single item is too large for our cache, don't store it
    if (size > MAX_CACHE_SIZE) {
      console.warn(`Graph is too large to cache (${(size / (1024 * 1024)).toFixed(2)} MB)`);
      return false;
    }

    const item = {
      id,
      query,
      dashboard,
      graphId,
      graphData,
      size,
      timestamp: Date.now()
    };

    try {
      const transaction = db.transaction([GRAPH_STORE], 'readwrite');
      const store = transaction.objectStore(GRAPH_STORE);

      await new Promise((resolve, reject) => {
        const request = store.put(item);
        request.onsuccess = () => {
          cacheSize += size;
          console.log(`Stored graph in cache: ${id} (${(size / (1024 * 1024)).toFixed(2)} MB)`);
          resolve();
        };
        request.onerror = event => {
          console.error('Error storing graph in cache:', event.target.error);
          reject(event.target.error);
        };
      });

      // Clean up if we've exceeded our limits
      await cleanupCache();
      return true;
    } catch (error) {
      console.error('Failed to store graph in cache:', error);
      return false;
    }
  };

  // Retrieve a graph from the cache
  cacheSelf.getGraph = async (query, dashboard, graphId) => {
    const id = generateCacheId(query, dashboard, graphId);

    try {
      const transaction = db.transaction([GRAPH_STORE], 'readonly');
      const store = transaction.objectStore(GRAPH_STORE);

      const graph = await new Promise((resolve, reject) => {
        const request = store.get(id);
        request.onsuccess = () => resolve(request.result);
        request.onerror = event => {
          console.error('Error retrieving graph from cache:', event.target.error);
          reject(event.target.error);
        };
      });

      if (graph) {
        console.log(`Retrieved graph from cache: ${id}`);
        // Update the timestamp to keep this item in cache
        updateTimestamp(id);
        return graph.graphData;
      }

      return null;
    } catch (error) {
      console.error('Failed to retrieve graph from cache:', error);
      return null;
    }
  };

  // Clear the entire cache
  cacheSelf.clearCache = async () => {
    try {
      const transaction = db.transaction([GRAPH_STORE], 'readwrite');
      const store = transaction.objectStore(GRAPH_STORE);

      await new Promise((resolve, reject) => {
        const request = store.clear();
        request.onsuccess = () => {
          console.log('Graph cache cleared');
          cacheSize = 0;
          resolve();
        };
        request.onerror = event => {
          console.error('Error clearing graph cache:', event.target.error);
          reject(event.target.error);
        };
      });

      return true;
    } catch (error) {
      console.error('Failed to clear graph cache:', error);
      return false;
    }
  };

  // Get cache statistics
  cacheSelf.getStats = async () => {
    try {
      const transaction = db.transaction([GRAPH_STORE], 'readonly');
      const store = transaction.objectStore(GRAPH_STORE);

      const count = await new Promise(resolve => {
        const request = store.count();
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => resolve(0);
      });

      return {
        itemCount: count,
        totalSize: cacheSize,
        maxSize: MAX_CACHE_SIZE,
        percentUsed: ((cacheSize / MAX_CACHE_SIZE) * 100).toFixed(2)
      };
    } catch (error) {
      console.error('Failed to get cache stats:', error);
      return {
        itemCount: 0,
        totalSize: 0,
        maxSize: MAX_CACHE_SIZE,
        percentUsed: 0
      };
    }
  };

  // Create a cache control panel for debugging
  cacheSelf.createCacheControlPanel = () => {
    if (document.getElementById('cache-control-panel')) {
      return; // Panel already exists
    }

    const panel = document.createElement('div');
    panel.id = 'cache-control-panel';
    panel.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      background-color: #f8f9fa;
      border: 1px solid #dee2e6;
      border-radius: 8px;
      padding: 15px;
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
      z-index: 1000;
      font-family: sans-serif;
      max-width: 300px;
      display: none;
    `;

    panel.innerHTML = `
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
        <h3 style="margin: 0; font-size: 16px;">Graph Cache Control</h3>
        <button id="cache-panel-close" style="border: none; background: none; cursor: pointer; font-size: 16px;">×</button>
      </div>
      <div id="cache-stats" style="margin-bottom: 15px; font-size: 14px;">
        <div>Loading cache statistics...</div>
      </div>
      <div style="display: flex; gap: 10px;">
        <button id="clear-cache-btn" class="mdl-button mdl-js-button mdl-button--raised mdl-button--colored" style="flex: 1;">Clear Cache</button>
        <button id="refresh-cache-stats-btn" class="mdl-button mdl-js-button mdl-button--raised" style="flex: 1;">Refresh Stats</button>
      </div>
    `;

    document.body.appendChild(panel);

    // Add event listeners
    document.getElementById('cache-panel-close').addEventListener('click', () => {
      panel.style.display = 'none';
    });

    document.getElementById('clear-cache-btn').addEventListener('click', async () => {
      const result = await cacheSelf.clearCache();
      if (result) {
        document.getElementById('cache-stats').innerHTML = '<div>Cache cleared successfully!</div>';
        setTimeout(updateStats, 1000);
      } else {
        document.getElementById('cache-stats').innerHTML =
          '<div style="color: red;">Failed to clear cache</div>';
      }
    });

    document.getElementById('refresh-cache-stats-btn').addEventListener('click', updateStats);

    // Update stats function
    async function updateStats() {
      const stats = await cacheSelf.getStats();
      document.getElementById('cache-stats').innerHTML = `
        <div><strong>Items:</strong> ${stats.itemCount} / ${MAX_CACHED_ITEMS}</div>
        <div><strong>Size:</strong> ${(stats.totalSize / (1024 * 1024)).toFixed(2)} MB / ${(stats.maxSize / (1024 * 1024)).toFixed(0)} MB</div>
        <div><strong>Usage:</strong> ${stats.percentUsed}%</div>
      `;
    }

    // Initial stats update
    updateStats();

    // Admin can access through console by calling graphCache.toggleControlPanel()

    return panel;
  };

  // Add a helper to toggle control panel
  cacheSelf.toggleControlPanel = () => {
    const panel =
      document.getElementById('cache-control-panel') || cacheSelf.createCacheControlPanel();
    panel.style.display = panel.style.display === 'none' ? 'block' : 'none';
  };

  // Create a dev button that only shows up when ?dev=true in URL params
  cacheSelf.createDevCacheButton = () => {
    // Check if dev=true exists in URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('dev') !== 'true') {
      return null; // Don't create button if not in dev mode
    }

    // Create the button if it doesn't exist
    if (document.getElementById('dev-clear-cache-btn')) {
      return document.getElementById('dev-clear-cache-btn');
    }

    const button = document.createElement('button');
    button.id = 'dev-clear-cache-btn';
    button.className = 'mdl-button mdl-js-button mdl-button--raised mdl-button--accent';
    button.innerText = 'Clear Graph Cache';
    button.style.cssText = `
      position: fixed;
      top: 10px;
      right: 10px;
      z-index: 1000;
    `;

    button.addEventListener('click', async () => {
      const result = await cacheSelf.clearCache();
      if (result) {
        // Show a snackbar or alert that cache was cleared
        if (window.showSnackbar) {
          window.showSnackbar('Graph cache cleared successfully');
        } else {
          alert('Graph cache cleared successfully');
        }
      } else {
        if (window.showSnackbar) {
          window.showSnackbar('Failed to clear graph cache', true);
        } else {
          alert('Failed to clear graph cache');
        }
      }
    });

    document.body.appendChild(button);
    return button;
  };

  // Helper method for tests to reset state
  cacheSelf._resetForTests = () => {
    db = null;
    cacheSize = 0;
  };

  // Check if localStorage is available in the browser
  const isLocalStorageAvailable = (() => {
    try {
      localStorage.setItem('test', 'test');
      localStorage.removeItem('test');
      return true;
    } catch (e) {
      return false;
    }
  })();

  // If localStorage is not available, create a fallback implementation
  if (!isLocalStorageAvailable) {
    console.warn('localStorage not available, using in-memory storage fallback');
    let memoryStorage = {};
    window.localStorage = {
      getItem: key => memoryStorage[key] || null,
      setItem: (key, value) => {
        memoryStorage[key] = String(value);
      },
      removeItem: key => {
        delete memoryStorage[key];
      },
      clear: () => {
        memoryStorage = {};
      },
      key: index => Object.keys(memoryStorage)[index] || null,
      length: () => Object.keys(memoryStorage).length
    };
  }

  // Self-test function to validate the module works correctly
  cacheSelf.runSelfTest = async () => {
    console.log('🧪 Running graph cache self-test...');
    try {
      // Initialize the cache
      await cacheSelf.init();
      console.log('✅ Cache initialized');

      // Store a test graph
      const testGraph = {
        plotlyJSON: { data: [{ x: [1, 2, 3], y: [4, 5, 6], type: 'scatter' }] },
        metadata: { df_filename: 'test.csv' },
        plotlyChartType: 'scatter'
      };

      await cacheSelf.storeGraph('test query', 'dashboard1', 'graph1', testGraph);
      console.log('✅ Test graph stored');

      // Retrieve the test graph
      const retrievedGraph = await cacheSelf.getGraph('test query', 'dashboard1', 'graph1');
      console.log('✅ Test graph retrieved:', retrievedGraph ? 'Success' : 'Failed');

      // Get cache stats
      const stats = await cacheSelf.getStats();
      console.log('✅ Cache stats:', stats);

      // Clear the cache
      await cacheSelf.clearCache();
      console.log('✅ Cache cleared');

      // Final verification
      const emptyCheck = await cacheSelf.getGraph('test query', 'dashboard1', 'graph1');
      console.log('✅ Verification after clear:', emptyCheck === null ? 'Success' : 'Failed');

      console.log('🎉 Graph cache self-test completed successfully!');
      return true;
    } catch (error) {
      console.error('❌ Graph cache self-test failed:', error);
      return false;
    }
  };

  return cacheSelf;
})();

if (typeof window !== 'undefined') {
  window.graphCache = graphCache;
}
